import type { Channel, ChannelStatus } from "./types";

/** UI pill / filter labels used by the locked channel pages. */
export type UiChannelStatus =
  | "LIVE"
  | "NOT LIVE"
  | "APPROVED"
  | "PENDING"
  | "DISABLED";

const BACKEND_TO_UI: Record<ChannelStatus, UiChannelStatus> = {
  LIVE: "LIVE",
  NOT_LIVE: "NOT LIVE",
  APPROVED: "APPROVED",
  PENDING: "PENDING",
  DISABLED: "DISABLED",
};

const UI_TO_BACKEND: Record<UiChannelStatus, ChannelStatus> = {
  LIVE: "LIVE",
  "NOT LIVE": "NOT_LIVE",
  APPROVED: "APPROVED",
  PENDING: "PENDING",
  DISABLED: "DISABLED",
};

export function toUiStatus(status: ChannelStatus | string | null | undefined): UiChannelStatus {
  if (!status) return "PENDING";
  if (status === "NOT LIVE") return "NOT LIVE";
  const mapped = BACKEND_TO_UI[status as ChannelStatus];
  if (mapped) return mapped;
  // Tolerate already-UI or unknown values without crashing pills.
  if (status === "LIVE" || status === "APPROVED" || status === "PENDING" || status === "DISABLED") {
    return status;
  }
  return "PENDING";
}

export function fromUiStatus(status: UiChannelStatus | string): ChannelStatus {
  if (status === "NOT_LIVE") return "NOT_LIVE";
  const mapped = UI_TO_BACKEND[status as UiChannelStatus];
  if (mapped) return mapped;
  return "PENDING";
}

/**
 * Dashboard / glance song & rotation counts:
 * prefer production when a production schedule exists, else staging.
 */
export function glanceCounts(channel: Channel): { songs: number; rotations: number } {
  if (channel.productionScheduleId) {
    return {
      songs: channel.productionSongCount ?? 0,
      rotations: channel.productionRotationCount ?? 0,
    };
  }
  return {
    songs: channel.stagingSongCount ?? 0,
    rotations: channel.stagingRotationCount ?? 0,
  };
}

/** Format API timestamps the way Channel Admin / Dashboard already display dates. */
export function formatChannelUpdatedAt(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
}
